// Who registered at one activation, and one of them in full.
//
// THE READ NOTHING IN THIS SERVICE HAD. docs/console-people-activity.md is the
// audit behind this file and its first finding is that the attendee records
// have been in `spine_live` all along with no endpoint in front of them. The
// second finding is the one this file is shaped by: the screen that was drawn
// for them wants nine facts and the service holds five, and the four it does
// not hold are the four a reader would act on. So the interesting decisions
// here are all about absence.
//
// ---------------------------------------------------------------------------
// WHAT "VERIFIED REGISTRANTS ONLY" MEANS, BECAUSE IT DOES NOT MEAN WHAT IT
// SOUNDS LIKE, AND READING IT THE OTHER WAY WOULD EMPTY THIS REPORT.
//
// It does not mean `identity_assurance = 'mailbox_verified'`. That value is a
// function of the capture channel, so filtering on it would drop every
// registration taken on a stand tablet, which at a real show is most of them,
// and it would present as an activation where nobody registered.
//
// It means a registration that completed. The design shows a person the moment
// an address is entered, before any passcode is used, and the audit's finding is
// that no such person exists here: an abandoned address is an HMAC digest in
// `attendee_email_verification` and it is swept within a day. There is nothing
// to show and manufacturing something would mean storing addresses before they
// are proved, which is a retention decision rather than a screen's.
//
// So the subset is the latest `registration_completed` per attendee out of
// `spine_live`, `identity_assurance` is reported rather than filtered on, and
// the page says so in a sentence. A subset a reader cannot see the edges of is a
// subset they will mistake for the whole.
//
// ---------------------------------------------------------------------------
// THE EXPORT'S WITHHOLDING RULE IS NOT APPLIED HERE, AND THAT IS A DECISION
// RATHER THAN AN OMISSION.
//
// exportRule.ts withholds a person's contact details unless a live consent says
// otherwise, and the function that does it is called `discloseToClient` for a
// reason: it governs a file that leaves the building. This is the opposite
// position. The reader is a Nova admin looking at Nova's own processing records
// on a route a client viewer is refused outright, and the acts that reader
// performs on a record are corrections, withdrawals and erasure requests, none
// of which can be performed against a person whose name the console has hidden.
// A withdrawal that made somebody unfindable would be a withdrawal nobody could
// action.
//
// What stands in the rule's place is two other things, and they are named
// because "no rule applied" would otherwise be the accurate summary. The route
// is nova_admin only. And every read of it writes a `report_access` row before
// a row of personal data is fetched.
//
// IT WAS THREE UNTIL THE SCREEN WAS BUILT. The third was that the consent state
// per purpose travelled on every row of the list, so that a reader always had in
// front of them what could be done with what they were looking at. The list does
// not draw it and the design's list has no column for it: consent is on the
// record, where an act is performed. A safeguard nothing renders is not a
// safeguard, it is a map of everybody's consents crossing the wire on a bulk
// read to be dropped by the browser, so it was taken off the row rather than
// left to make this paragraph true on paper. The record still carries it, and
// carries the events and the wording besides.
//
// ---------------------------------------------------------------------------
// THE LIST CARRIES THE TWO SLOTS IT DRAWS AND THE RECORD CARRIES THE WHOLE FORM.
//
// `payload.contact` is four typed slots and `payload.fields` is whatever this
// activation's form asked for, which is where company, country, job title and
// every survey answer live. The list carries two of the first and none of the
// second, and the split is deliberate in every direction.
//
// The two it carries are the name and the contact address, which are the two the
// table draws and the two the search box matches on. The telephone number and
// the address things are sent to are not drawn, not searched, and are on the
// record a click away, so sending them with every row of a bulk read bought
// nothing and disclosed two more columns per person.
//
// A bulk read of an activation should not be the thing that hands over free
// text. survey.ts counts written answers and never quotes them, on the ground
// that a written answer is personal data, and a list endpoint that returned the
// whole fields map would quietly become the quoting surface that file refuses to
// be. One person's record does carry it, because that is what a record is, and
// because a field a reader cannot see is a field they cannot correct.
//
// ---------------------------------------------------------------------------
// WHY THIS ONE TAKES ARGUMENTS WHEN opt-in AND survey REFUSE TO.
//
// Those two are counts, and optIn.ts's argument is that a per-purpose count over
// a slice narrow enough identifies somebody, so they offer nothing to slice
// with. That argument does not transfer, and it is worth saying why rather than
// leaving the inconsistency for somebody to resolve in the wrong direction: this
// endpoint returns the people themselves, so narrowing it cannot reveal anything
// the unnarrowed answer did not already contain. There is no aggregate here for
// a filter to sharpen into an identification.
//
// The page ceiling is therefore about something else entirely, and it is a
// property of the audit rather than of the disclosure. `report_access.row_count`
// is how much left, so a page is the unit the audit counts in: a caller who
// wants everybody pages, and each page files its own row with its own size.
// An unbounded limit would make one row cover an entire activation.

import { toSafeInteger, utcIso, type Queryable } from "../../api/src/db.js";
import { describeAccess, requireAccess, type AccessPosition } from "./access.js";
import { consentStates } from "./export.js";
import type { ConsentStates } from "./exportRule.js";
import { readListOrder, type ListDir, type ListOrder } from "./listOrder.js";

/**
 * How many people one request may name.
 *
 * Fifty rather than everybody, and two hundred rather than no ceiling at all.
 * The numbers are sized on the audit rather than on a screen: the design's pager
 * opens at ten, which is a display preference, and what these decide is how
 * coarse a `report_access` row is allowed to be.
 */
export const ATTENDEE_PAGE_DEFAULT = 50;
export const ATTENDEE_PAGE_MAX = 200;

/**
 * The caller asked for something this endpoint will not answer.
 *
 * A refusal about arguments rather than about access, which is why it is its own
 * class and not an AccessDenied. reports/routes.ts turns it into a 400 in front
 * of `beginAccess`, on the reasoning the export's purpose check already carries:
 * 011's outcome vocabulary is answered, refused and failed, and none of those
 * three words describes a caller typing a page size of minus one. Filing it as
 * `failed` would put ordinary typos in the column an operator watches for this
 * service breaking.
 */
export class AttendeeQueryRefused extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = "AttendeeQueryRefused";
  }
}

/**
 * One person on the list.
 *
 * `email_verified_at` sits beside `identity_assurance` rather than being derived
 * from it, exactly as the export carries both. The schema's own note says the
 * assurance is stored though derivable, because it stops being derivable the
 * first time somebody builds a crew-assisted web flow, and a screen that
 * computed the moment from the word would then be recomputing history.
 */
export type AttendeeLine = {
  attendee_ref: string;
  registered_at: string;
  /** stand_tablet, self_serve_web or onsite_web. Three, where the design drew two. */
  capture_channel: string | null;
  identity_assurance: string | null;
  /** Null means no address was ever proved, which is not the same as an address that failed. */
  email_verified_at: string | null;
  display_name: string | null;
  contact_email: string | null;
  // The telephone number, the delivery address and the consent map were here.
  // See the note at the top: none of the three is drawn by the list or the
  // design's list, all three are on the record, and a row of a bulk read is the
  // worst place to carry a field nothing renders.
  /** What an Admin has done to this record (152), as it stands now. */
  standing: AttendeeStanding;
};

/**
 * Where one person stands after what an Admin has done to their record (152).
 *
 * Four flags rather than one state, because they are independent: a person can
 * be withheld from files and refused a passcode at once. Erased outranks the
 * rest in what a screen draws, since nothing further is recorded about them.
 */
export type AttendeeStanding = {
  withheld: boolean;
  blocked: boolean;
  erased: boolean;
  corrected: boolean;
};

/** One thing an Admin did to this record, from attendee_action. */
export type AttendeeActionLine = {
  action: "withhold" | "release" | "block" | "unblock" | "correct" | "erase";
  at: string;
  reason: string | null;
  /** The Admin who did it, as the identity this service proved. */
  by_identity_id: string;
  /**
   * For a correction, the fields it moved and from what to what. Null once an
   * erasure has scrubbed it, which `scrubbed_at` says.
   */
  correction: Record<string, { from: string | null; to: string | null }> | null;
  scrubbed_at: string | null;
  /** True where an erasure scrubbed the reason this row was given (153). */
  reason_scrubbed: boolean;
};

const NOT_ACTED_ON: AttendeeStanding = Object.freeze({
  withheld: false,
  blocked: false,
  erased: false,
  corrected: false,
});

/** Where a page sits, so a caller can ask for the next one without guessing. */
export type AttendeePagePosition = {
  limit: number;
  offset: number;
  returned: number;
  /** Everybody in the subset, not everybody on this page. */
  total: number;
  more: boolean;
};

/**
 * A fact the design wants and this service does not hold.
 *
 * CARRIED IN THE RESPONSE RATHER THAN LEFT TO A SCREEN, and this is the whole
 * mechanism by which the audit's second step gets built honestly. The four gaps
 * it names are all fields whose absence renders as a reassuring answer: a null
 * deliverability draws as "OK", a missing withhold flag draws as "in files", and
 * both are lies a reader would act on. Naming them here means the screen can
 * state them rather than draw them, and means nobody has to remember which
 * columns were never real.
 */
export type AttendeeGap = {
  /** The design's own words for the thing, so the two can be matched up. */
  what: string;
  why: string;
};

export type AttendeePage = {
  activation_id: string;
  as_at: string;
  access: AccessPosition;
  access_note: string;
  attendees: AttendeeLine[];
  page: AttendeePagePosition;
  /** Who is in this subset and who the design would have shown who is not. */
  subset_note: string;
  // No consent_note, because there is no consent map on a list row for it to
  // explain. It went with the map. The record still carries both, and there the
  // sentence is the most load-bearing one on the screen.
  cannot_answer: AttendeeGap[];
};

/** One consent act, with the paragraph it was given in. */
export type AttendeeConsentEvent = {
  at: string;
  event: "consent_captured" | "consent_withdrawn";
  purpose: string | null;
  consent_version_id: string | null;
  method: string | null;
  capture_channel: string | null;
  /**
   * The exact text this person was shown, or null.
   *
   * Null on a withdrawal, which cites no wording because there is no paragraph
   * to withdraw in: `event` is what tells the two apart. A consent whose hash the
   * table cannot produce would also read null, and 005's trigger refuses that
   * pairing at write time, so it is unreachable rather than merely unlikely.
   */
  wording: { wording_hash: string; locale: string; body: string } | null;
};

/** One passcode row's own timestamps, and nothing else off that row. */
export type AttendeePasscode = {
  verification_id: string;
  issued_at: string;
  /** When it was due to stop working. A scheduled moment rather than a recorded one. */
  expires_at: string;
  /** When the mailbox was proved. Null means this passcode was never spent. */
  consumed_at: string | null;
  /** When a resend replaced it. */
  superseded_at: string | null;
  /** When the ticket this proof bought was spent on a registration. */
  ticket_spent_at: string | null;
};

/**
 * What could be found out about this person's passcodes, or why nothing was
 * looked for.
 *
 * A UNION RATHER THAN A LIST THAT IS SOMETIMES EMPTY, and it is the most
 * important shape in this file. 114 stores an HMAC of the address rather than
 * the address, so `(activation_id, email_hash)` is the only way in and the
 * pepper is the only way to compute it. An instance without the pepper, and a
 * registration with no address on it, can both be answered only with "this was
 * not looked up". An empty array would say something quite different and much
 * worse: that this person was never sent a passcode.
 */
export type AttendeePasscodeAccount =
  | { kind: "not_looked_up"; why: string; says: string }
  | { kind: "read"; passcodes: AttendeePasscode[]; says: string };

export type AttendeeRecord = {
  activation_id: string;
  attendee_ref: string;
  as_at: string;
  access: AccessPosition;
  access_note: string;
  registration: {
    registered_at: string;
    capture_channel: string | null;
    /** Where the person was, which is not the channel. A crew member handing over an iPad is both. */
    surface: string | null;
    capture_mode: string | null;
    /**
     * Who helped, where anybody did.
     *
     * The nearest fact to the design's Crew column and deliberately not the same
     * one: it records that a crew member assisted a registration, not that the
     * person registering was crew. See `cannot_answer`.
     */
    assisted_by: string | null;
    identity_assurance: string | null;
    email_verified_at: string | null;
    field_set_version: number | null;
    /** Which published configuration this was captured under, where the channel records one. */
    captured_under_config_version: number | null;
    contact: {
      display_name: string | null;
      contact_email: string | null;
      deliver_to_email: string | null;
      phone: string | null;
    };
    /** Whatever this activation's form asked for, as it was captured. */
    fields: Record<string, unknown>;
  };
  /** The latest word per purpose, the same map and the same rule as the list's. */
  consent: ConsentStates;
  consent_events: AttendeeConsentEvent[];
  passcodes: AttendeePasscodeAccount;
  consent_note: string;
  /** What an Admin has done to this record (152), as it stands now. */
  standing: AttendeeStanding;
  /** Everything an Admin has done to it, oldest first. The record of it is the audit. */
  actions: AttendeeActionLine[];
  /** What the standing means, in a sentence, for whoever reads it. */
  standing_note: string;
  /** For an erased person, what the erasure reached (153). Null for anybody else. */
  erasure: AttendeeErasure | null;
  cannot_answer: AttendeeGap[];
};

/** What one erasure reached, from attendee_erasure. */
export type AttendeeErasure = {
  spine_rows: number;
  entry_guests: number;
  booth_media: number;
  booth_bytes_deleted: number | null;
  devices_told: number;
  reasons_scrubbed: number;
  /** Ride to Survive in FortNine: done, still waiting, or not reachable from this service. */
  ride_state: "pending" | "done" | "not_configured";
  ride_attempts: number;
  ride_erased: number | null;
  ride_done_at: string | null;
  recorded_at: string;
};

/**
 * How an address becomes the digest 114 holds, supplied by whoever has the
 * pepper.
 *
 * A FUNCTION RATHER THAN THE SECRET, so that this package never holds one. It is
 * `hashAttendeeEmail` from the attendee surface, which is the same computation
 * that wrote the row, passed in rather than reimplemented: two implementations
 * of one digest is a lookup that silently finds nothing.
 *
 * Uint8Array here and a Buffer where it is supplied, and the split is forced
 * rather than chosen. This package compiles with `types: []` because nothing in
 * it should know what a runtime is, so Buffer cannot be named in this file. The
 * distinction still matters: node-postgres recognises a Buffer as a bytea
 * parameter and JSON-stringifies anything else, so a plain Uint8Array would go
 * down the wire as a string of digits, match no row, and report a person as
 * having been sent no passcode. reports/routes.ts narrows the option it takes to
 * Buffer, which is the layer that has the word.
 */
export type AttendeeEmailDigest = (email: string) => Uint8Array;

export type AttendeeListOptions = {
  now?: Date;
  limit?: unknown;
  offset?: unknown;
  /** One of ATTENDEE_SORT_KEYS, or absent for the order people arrived in. */
  sort?: unknown;
  /** asc or desc, and only beside a sort. */
  dir?: unknown;
};

/**
 * The People list's columns, each sortable on the server (12 Oct hand-off:
 * every column sorts, and the sort orders every page rather than the one on
 * screen). Person, Lane, Address and Registered, so "every column sorts" is the
 * length of this array; Open is a control and has nothing to order by.
 */
export const ATTENDEE_SORT_KEYS = ["person", "lane", "address", "registered"] as const;
export type AttendeeSortKey = (typeof ATTENDEE_SORT_KEYS)[number];
export type AttendeeOrder = ListOrder<AttendeeSortKey>;

/**
 * What the Lane cell prints for each capture channel. Here rather than in the
 * console so the column sorts by the words the reader sees, and the console
 * prints from this same map: a sort by the stored word would put "Web, with a
 * passcode" (self_serve_web) between "Onsite web" and "Stand tablet".
 */
export const ATTENDEE_LANE_WORDS: Readonly<Record<string, string>> = Object.freeze({
  stand_tablet: "Stand tablet",
  onsite_web: "Onsite web",
  self_serve_web: "Web, with a passcode",
});

export type AttendeeRecordOptions = {
  now?: Date;
  /** Null where this instance was never given the attendee pepper. */
  digestEmail?: AttendeeEmailDigest | null;
};

// ------------------------------------------------------------------- the gaps
//
// One frozen set, so the list and the record cannot describe the same absence
// differently, and so a gap that gets filled in later is deleted from one place.

const GAPS = Object.freeze({
  passcodeNotUsed: Object.freeze({
    what: "Passcode not used, as a state a person can be in",
    why:
      "This service holds no person until a registration completes. An address somebody typed and " +
      "abandoned exists only as a digest in the verification table and is swept within a day, so there " +
      "is nobody to list. Showing one would mean keeping addresses before they are proved, which is a " +
      "retention decision rather than a reporting one.",
  }),
  crewAsAKind: Object.freeze({
    what: "Crew, as a kind of person",
    why:
      "There is no attendee kind. Everybody here registered as an attendee. The nearest recorded fact is " +
      "assisted_by on a person's own record, which says a crew member helped with the registration, and " +
      "that is a different fact about a different person.",
  }),
  deliverability: Object.freeze({
    what: "Email deliverability",
    why:
      "The verification send answers identically whatever becomes of the mail, on purpose, so nothing " +
      "here knows whether an address accepted anything. asset_delivered.outcome carries bounced, and " +
      "that is asset delivery to a screen rather than a passcode to a person.",
  }),
  filesThisPersonIsIn: Object.freeze({
    what: "Files this person is in",
    why:
      "Exports are recorded per activation. The record of one is report_access.detail, which carries the " +
      "template, the columns and a row count and deliberately names nobody, so it cannot answer whether a " +
      "particular person was in a particular file. Answering it would mean writing attendee references " +
      "into the audit table, which would make it a second copy of what was exported.",
  }),
  recentActivity: Object.freeze({
    what: "This person's own activity, as one feed",
    why:
      "There is a feed now, and it is the activation's rather than a person's: activity.ts unions the " +
      "spine, the published bundles and the recorded export reads, and nothing offers a slice of it for " +
      "one attendee. What it leaves out it leaves out permanently, so a per-person view built on it would " +
      "still be missing the kiosk tables and the tokens, which no role on this surface may read, and the " +
      "passcodes, which are swept within about a day. The consent history below is the part of this " +
      "person's story that is complete, and it is complete because every consent act is on the spine.",
  }),
  passcodePlace: Object.freeze({
    what: "Where a passcode was issued",
    why:
      "The verification table holds no surface and no station. It exists only for the emailed-passcode " +
      "web flow, so the answer for every row it has would be the same one, and it has no row at all for a " +
      "registration taken on a stand tablet.",
  }),
  passcodeExpired: Object.freeze({
    what: "Expired unused, as something that happened",
    why:
      "No row is written when a passcode expires. The moment it was due to stop working is on the row as " +
      "expires_at and the sweep removes it later, so an expiry is implied by two timestamps rather than " +
      "recorded as an event. The timestamps are here and the event is not.",
  }),
});

const LIST_GAPS: readonly AttendeeGap[] = Object.freeze([
  GAPS.passcodeNotUsed,
  GAPS.crewAsAKind,
  GAPS.deliverability,
]);

const RECORD_GAPS: readonly AttendeeGap[] = Object.freeze([
  GAPS.crewAsAKind,
  GAPS.deliverability,
  GAPS.filesThisPersonIsIn,
  GAPS.recentActivity,
  GAPS.passcodePlace,
  GAPS.passcodeExpired,
]);

const CONSENT_NOTE =
  "The consent map is the latest word per purpose, ordered by when the person acted rather than by when " +
  "this service heard about it. A purpose that is absent from the map has no consent event of any kind, " +
  "and that is not a refusal: there is no event for a refusal, so a purpose nobody was asked about and a " +
  "purpose everybody declined are indistinguishable from here. Read an absent purpose as unknown, never " +
  "as no.";

// ------------------------------------------------------------- what a caller asked for

/**
 * The page, resolved before anything is read.
 *
 * Called by the route in front of `beginAccess` so that a malformed request
 * costs no audit row, and called again in here on the reasoning
 * exportActivation gives about its own arguments: this is a function that hands
 * over personal data and the one thing it must not do is assume its arguments
 * have been through a validator it cannot see.
 *
 * `undefined` is no preference and gets the default. Anything present is
 * checked, so `?limit=` is a refusal rather than a silent fallback to fifty: a
 * parameter sent empty is far more likely to be a caller assembling a query
 * string badly than a request for the default, and this is not the endpoint to
 * be generous on.
 */
export function resolveAttendeePage(options: AttendeeListOptions): {
  limit: number;
  offset: number;
  order: AttendeeOrder | null;
} {
  // The order is settled with the page and for the same reason: in front of
  // the audit row, so a request for a column this list does not have is a 400
  // that costs no report_access row, because nothing was read.
  const order = readListOrder({ sort: options.sort, dir: options.dir }, ATTENDEE_SORT_KEYS);
  if (!order.ok) throw new AttendeeQueryRefused(order.detail);
  return {
    limit: whole(options.limit, "limit", ATTENDEE_PAGE_DEFAULT, 1, ATTENDEE_PAGE_MAX),
    offset: whole(options.offset, "offset", 0, 0, Number.MAX_SAFE_INTEGER),
    order: order.order,
  };
}

function whole(raw: unknown, what: string, fallback: number, least: number, most: number): number {
  if (raw === undefined || raw === null) return fallback;

  const value = typeof raw === "number" ? raw : Number(String(raw).trim());
  if (String(raw).trim() === "" || !Number.isSafeInteger(value)) {
    throw new AttendeeQueryRefused(
      `${what} is ${JSON.stringify(raw)}, which is not a whole number. Omit it for the default of ${fallback}.`,
    );
  }
  if (value < least || value > most) {
    throw new AttendeeQueryRefused(
      `${what} is ${value} and this endpoint answers ${least} to ${most}. ` +
        `A page is what the audit counts in, so there is a ceiling on how much one recorded read may carry.`,
    );
  }
  return value;
}

/**
 * An attendee reference, shape-checked before it becomes a parameter.
 *
 * `spine.attendee_ref` is a uuid, so an unchecked value reaches Postgres as a
 * failed cast rather than as an empty result: a 500 and an audit row reading
 * `failed`, for what is almost always a truncated paste. Checked in front of the
 * audit for the reason `resolveAttendeePage` is, and checked again in here for
 * the same reason it is.
 */
export function resolveAttendeeRef(raw: unknown): string {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!UUID.test(value)) {
    throw new AttendeeQueryRefused(
      "that is not an attendee reference. A reference is the uuid this activation's own records carry, " +
        "and it means nothing outside the activation it belongs to.",
    );
  }
  return value.toLowerCase();
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ------------------------------------------------------------------- the list

export async function readAttendees(
  c: Queryable,
  activationId: string,
  options: AttendeeListOptions = {},
): Promise<AttendeePage> {
  // Resolved first, so a bad page costs no query over attendee rows. The export
  // makes the same move for the same reason: a refusal that costs a read of the
  // thing it was refusing to hand over has already read it.
  const { limit, offset, order } = resolveAttendeePage(options);

  // And access before counting, which is the rule every surface in this package
  // opens with. Row-level security answers the wrong reader with fewer rows and
  // no error, so an empty page and a refused one are the same page unless
  // something makes them different.
  const access = await requireAccess(c, activationId);
  const now = options.now ?? new Date();

  const { rows } = await c.query<ListRow>(listStatement(order), [activationId, limit, offset]);

  const attendees = rows.map(
    (row): AttendeeLine => ({
      attendee_ref: row.attendee_ref,
      registered_at: row.registered_at,
      capture_channel: row.capture_channel ?? null,
      identity_assurance: row.identity_assurance ?? null,
      email_verified_at: row.email_verified_at ?? null,
      display_name: row.display_name ?? null,
      contact_email: row.contact_email ?? null,
      standing: {
        withheld: row.withheld === true,
        blocked: row.blocked === true,
        erased: row.erased === true,
        corrected: row.corrected === true,
      },
    }),
  );

  // Off the first row rather than counted again, because the total travels in
  // the same statement as the page. Two statements would be two snapshots under
  // read committed, and a count that disagrees with the rows beside it is the
  // shape of discrepancy somebody spends an afternoon on.
  const total = rows.length === 0 ? await countAttendees(c, activationId) : toSafeInteger(rows[0]!.total, "total");

  return {
    activation_id: activationId,
    as_at: now.toISOString(),
    access,
    access_note: describeAccess(access),
    attendees,
    page: {
      limit,
      offset,
      returned: attendees.length,
      total,
      more: offset + attendees.length < total,
    },
    subset_note:
      "Everybody whose registration completed at this activation, latest word per person, with voided " +
      "captures excluded. A re-registration is a correction the person made themselves, so what is here is " +
      "what they last told us rather than both attempts. identity_assurance says whether an address was " +
      "ever proved and is not a filter on this list: a registration taken on a stand tablet is " +
      "self_asserted by construction, and dropping those would hide most of a show. Nobody who started a " +
      "registration and did not finish it is here. Those addresses are held apart from this list and are " +
      "removed the moment a mailbox is proved.",
    cannot_answer: [...LIST_GAPS],
  };
}

type ListRow = {
  attendee_ref: string;
  registered_at: string;
  capture_channel: string | null;
  identity_assurance: string | null;
  email_verified_at: string | null;
  display_name: string | null;
  contact_email: string | null;
  withheld: boolean | null;
  blocked: boolean | null;
  erased: boolean | null;
  corrected: boolean | null;
  total: number | string;
};

/**
 * A sibling of export.ts's `readRows`, and the family resemblance is the point:
 * the two answer the same question about the same rows to two different readers,
 * and they must not disagree about who registered.
 *
 * Two things are shared with it deliberately. `distinct on (attendee_ref)`
 * taking the latest registration, because a re-registration is a correction.
 * And spine_live throughout, which is what makes a voided capture absent without
 * a line of code here remembering to exclude it.
 *
 * What differs is the paging, and the total is inside the same statement rather
 * than beside it so that the count and the rows are one snapshot.
 *
 * It also no longer aggregates consent. That was a third shared property and a
 * whole CTE, and the list drew none of it — readAttendee still orders a person's
 * consent by `occurred_at` before `spine_seq`, so a withdrawal taken offline at
 * eleven and synced at five still comes after a consent taken at ten.
 */
const LIST = `
  with registration as (
    select distinct on (l.attendee_ref)
      l.attendee_ref,
      l.spine_seq,
      ${utcIso("l.occurred_at")}                                  as registered_at,
      l.capture_channel::text                                     as capture_channel,
      l.payload ->> 'identity_assurance'                          as identity_assurance,
      ${utcIso("(l.payload ->> 'email_verified_at')::timestamptz")} as email_verified_at,
      attendee_contact(l.activation_id, l.attendee_ref, l.payload -> 'contact') ->> 'display_name'  as display_name,
      attendee_contact(l.activation_id, l.attendee_ref, l.payload -> 'contact') ->> 'contact_email' as contact_email,
      l.occurred_at                                               as occurred
    from spine_live l
    where l.activation_id = $1
      and l.event_type = 'registration_completed'
      and l.attendee_ref is not null
    order by l.attendee_ref, l.spine_seq desc
  ),
  page as (
    select r.* from registration r order by $ORDER limit $2 offset $3
  )
  select
    p.attendee_ref,
    p.registered_at,
    p.capture_channel,
    p.identity_assurance,
    p.email_verified_at,
    p.display_name,
    p.contact_email,
    s.withheld,
    s.blocked,
    s.erased,
    s.corrected,
    (select count(*) from registration) as total
  from page p
  left join attendee_standing s
    on s.activation_id = $1 and s.attendee_ref = p.attendee_ref
  order by $ORDER
`;

/**
 * How each sortable column of the People list is ordered, written out in full.
 *
 * THE REQUEST NAMES A KEY AND NEVER A COLUMN. resolveAttendeePage has already
 * refused any key off ATTENDEE_SORT_KEYS, this map is the only place a key
 * becomes SQL, and every value is a constant in this file, the direction
 * included (one of two literals, never the caller's string).
 *
 * Each orders by what its cell prints, over the columns the `registration` CTE
 * names, so one text orders the page out of the whole subset and then the page
 * itself (the expressions name no table, and both FROMs carry the columns):
 *
 *   - Person is the name, case-folded, then the address under it; somebody
 *     with no name given goes last either way, because a missing name is not
 *     the first name in the alphabet;
 *   - Lane is the words ATTENDEE_LANE_WORDS prints, not the stored channel;
 *   - Address is proved, then proved with no moment recorded, then not proved,
 *     the three tags the cell draws, and then the address;
 *   - Registered is when the person finished, on the spine's own clock.
 *
 * Whatever the key, the order finishes on the reference, so two people level on
 * a column never swap places between two pages of one walk.
 */
const ATTENDEE_ORDER: Readonly<Record<AttendeeSortKey, (dir: ListDir) => string>> = {
  person: (dir) =>
    `lower(nullif(btrim(display_name), '')) ${dir} nulls last, lower(nullif(btrim(contact_email), '')) ${dir} nulls last`,
  lane: (dir) => `lower(${laneWords()}) ${dir} nulls last`,
  address: (dir) =>
    `case when email_verified_at is not null then 2 when identity_assurance = 'mailbox_verified' then 1 else 0 end ${dir}, ` +
    `lower(nullif(btrim(contact_email), '')) ${dir} nulls last`,
  registered: (dir) => `occurred ${dir}`,
};

/** The Lane cell's words as SQL, built from the constant map and never from a request. */
function laneWords(): string {
  const arms = Object.entries(ATTENDEE_LANE_WORDS)
    .map(([channel, words]) => `when '${channel.replace(/'/g, "''")}' then '${words.replace(/'/g, "''")}'`)
    .join(" ");
  return `case capture_channel ${arms} else capture_channel end`;
}

/** The statement for one order, built once per key and direction and then reused. */
const STATEMENTS = new Map<string, string>();
function listStatement(order: AttendeeOrder | null): string {
  const name = order === null ? "" : `${order.key}.${order.dir}`;
  const held = STATEMENTS.get(name);
  if (held !== undefined) return held;
  // Null is the order people arrived in, exactly as before a list could be
  // sorted, so the console's first read asks what it always asked.
  const by =
    order === null
      ? "spine_seq"
      : `${ATTENDEE_ORDER[order.key](order.dir === "desc" ? "desc" : "asc")}, attendee_ref`;
  const text = LIST.split("$ORDER").join(by);
  STATEMENTS.set(name, text);
  return text;
}

/**
 * The total, for the one case the statement above cannot carry it in.
 *
 * A page past the end returns no rows, and a total that arrives on a row is a
 * total that arrives nowhere when there are none. Its own query rather than a
 * zero, because zero here would tell a caller who asked for offset 500 of an
 * activation with 600 people that the activation is empty.
 */
async function countAttendees(c: Queryable, activationId: string): Promise<number> {
  const { rows } = await c.query<{ total: number | string }>(
    `select count(distinct l.attendee_ref) as total
     from spine_live l
     where l.activation_id = $1
       and l.event_type = 'registration_completed'
       and l.attendee_ref is not null`,
    [activationId],
  );
  return toSafeInteger(rows[0]?.total ?? 0, "total");
}

// ----------------------------------------------------------------- one person

/**
 * One person's record, or null.
 *
 * NULL RATHER THAN A THROW, and the difference lands in the audit. A reference
 * nobody has is not a refusal and it is not this service failing: the read ran,
 * it disclosed nothing, and the honest row says answered with a count of zero.
 * A thrown class would arrive at reports/routes.ts as neither AccessDenied nor a
 * business state and be filed as `failed`, which is the column an operator
 * watches to find out that the service is broken.
 *
 * Scoped to the activation in the where clause and not filtered afterwards. 001
 * says an attendee reference is scoped to its activation and meaningless outside
 * it, and uniqueness is on the pair rather than on the reference alone, so
 * reading one activation's reference at another's url has to find nothing rather
 * than find a stranger.
 */
export async function readAttendee(
  c: Queryable,
  activationId: string,
  attendeeRef: unknown,
  options: AttendeeRecordOptions = {},
): Promise<AttendeeRecord | null> {
  const ref = resolveAttendeeRef(attendeeRef);
  const access = await requireAccess(c, activationId);
  const now = options.now ?? new Date();

  const { rows } = await c.query<RecordRow>(RECORD, [activationId, ref]);
  const row = rows[0];
  if (row === undefined) return null;

  const payload = asRecord(row.payload) ?? {};
  // The contact slots and the form answers as every surface shows them (152):
  // corrected where an Admin corrected them, and nothing once erased.
  const contact = asRecord(row.contact) ?? {};
  const fields = asRecord(row.fields) ?? {};
  const actions = await readActions(c, activationId, ref);
  const standing = standingFrom(actions);

  const consentEvents = await readConsentEvents(c, activationId, ref);
  // A scoped reader — a client viewer or an Editor, from 2026-10-11 — is not
  // shown the passcode history, and is told so rather than handed an empty
  // list. 114 gives xp_reader nothing on attendee_email_verification on purpose
  // (which activations an address was offered a code at is not a report about an
  // activation), so the read would fail rather than answer; and the record's
  // other half — who registered, what they said, what they consented to — is
  // what the ruling gave a Viewer.
  const passcodes =
    access.kind === "granted"
      ? ({
          kind: "not_looked_up",
          why: "scoped_reader",
          says:
            "Passcode history is kept for Nova's support desk and is not shown to a client's own reader. " +
            "Nothing was looked up. This is not a statement that no passcode was ever sent to this person.",
        } as const)
      : await readPasscodes(c, activationId, text(contact["contact_email"]), options.digestEmail ?? null);

  return {
    activation_id: activationId,
    attendee_ref: ref,
    as_at: now.toISOString(),
    access,
    access_note: describeAccess(access),
    registration: {
      registered_at: row.registered_at,
      capture_channel: row.capture_channel ?? null,
      surface: text(payload["surface"]),
      capture_mode: text(payload["capture_mode"]),
      assisted_by: text(payload["assisted_by"]),
      identity_assurance: text(payload["identity_assurance"]),
      email_verified_at: row.email_verified_at ?? null,
      field_set_version: number(payload["field_set_version"]),
      captured_under_config_version:
        row.captured_under_config_version === null
          ? null
          : toSafeInteger(row.captured_under_config_version, "captured_under_config_version"),
      contact: {
        display_name: text(contact["display_name"]),
        contact_email: text(contact["contact_email"]),
        deliver_to_email: text(contact["deliver_to_email"]),
        phone: text(contact["phone"]),
      },
      fields,
    },
    // Built from the events just read rather than by a second query, so one
    // person's summary and one person's history cannot disagree with each other.
    // It is the same map the list carries and it is produced by the same
    // function, which is what stops the two endpoints answering differently
    // about whether somebody consented.
    consent: consentStates(latestPerPurpose(consentEvents)),
    consent_events: consentEvents,
    passcodes,
    consent_note: CONSENT_NOTE,
    standing,
    actions,
    standing_note: standingNote(standing),
    erasure: standing.erased ? await readErasure(c, activationId, ref) : null,
    cannot_answer: [...RECORD_GAPS],
  };
}

/** What the erasure reached, for an erased person. */
async function readErasure(c: Queryable, activationId: string, attendeeRef: string): Promise<AttendeeErasure | null> {
  const { rows } = await c.query<Record<string, unknown>>(
    `select spine_rows, entry_guests, booth_media, booth_bytes_deleted, devices_told, reasons_scrubbed,
            ride_state, ride_attempts, ride_erased, ${utcIso("ride_done_at")} as ride_done_at,
            ${utcIso("recorded_at")} as recorded_at
       from attendee_erasure
      where activation_id = $1 and attendee_ref = $2`,
    [activationId, attendeeRef],
  );
  const row = rows[0];
  if (row === undefined) return null;
  const n = (key: string) => toSafeInteger(row[key] ?? 0, key);
  return {
    spine_rows: n("spine_rows"),
    entry_guests: n("entry_guests"),
    booth_media: n("booth_media"),
    booth_bytes_deleted: row["booth_bytes_deleted"] == null ? null : n("booth_bytes_deleted"),
    devices_told: n("devices_told"),
    reasons_scrubbed: n("reasons_scrubbed"),
    ride_state: row["ride_state"] as AttendeeErasure["ride_state"],
    ride_attempts: n("ride_attempts"),
    ride_erased: row["ride_erased"] == null ? null : n("ride_erased"),
    ride_done_at: (row["ride_done_at"] as string | null) ?? null,
    recorded_at: String(row["recorded_at"] ?? ""),
  };
}

/** Everything an Admin did to this record, oldest first. */
async function readActions(c: Queryable, activationId: string, attendeeRef: string): Promise<AttendeeActionLine[]> {
  const { rows } = await c.query<{
    action: AttendeeActionLine["action"];
    at: string;
    reason: string | null;
    by_identity_id: string;
    correction: AttendeeActionLine["correction"];
    scrubbed_at: string | null;
    reason_scrubbed: boolean;
  }>(
    `select x.action,
            (x.reason_scrubbed_at is not null) as reason_scrubbed,
            ${utcIso("x.acted_at")}         as at,
            x.reason,
            x.actor_identity_id::text       as by_identity_id,
            x.correction,
            ${utcIso("x.scrubbed_at")}      as scrubbed_at
       from attendee_action x
      where x.activation_id = $1 and x.attendee_ref = $2
      order by x.action_seq`,
    [activationId, attendeeRef],
  );
  return rows.map(({ reason_scrubbed, ...line }) => ({ ...line, reason_scrubbed }));
}

/** The latest of each pair decides, which is attendee_standing's rule restated over rows already read. */
function standingFrom(actions: readonly AttendeeActionLine[]): AttendeeStanding {
  let withheld = false;
  let blocked = false;
  for (const line of actions) {
    if (line.action === "withhold") withheld = true;
    if (line.action === "release") withheld = false;
    if (line.action === "block") blocked = true;
    if (line.action === "unblock") blocked = false;
  }
  return actions.length === 0
    ? { ...NOT_ACTED_ON }
    : {
        withheld,
        blocked,
        erased: actions.some((line) => line.action === "erase"),
        corrected: actions.some((line) => line.action === "correct"),
      };
}

function standingNote(standing: AttendeeStanding): string {
  if (standing.erased) {
    return (
      "This person's details were erased by an Admin and cannot be brought back. Their registration stays in " +
      "every count as a record with nobody's details on it, and nothing further is recorded about them. A " +
      "file produced before the erasure still holds what it held."
    );
  }
  const parts: string[] = [];
  if (standing.withheld) {
    parts.push("Withheld: this person is left out of every file produced from here on, and kept on this activation.");
  }
  if (standing.blocked) {
    parts.push("Refused: their address is refused a passcode, a registration and a play at this show.");
  }
  if (standing.corrected) {
    parts.push("Corrected: the details shown are the latest correction, and what they were is recorded below.");
  }
  return parts.length === 0 ? "Nothing has been done to this record." : parts.join(" ");
}

type RecordRow = {
  registered_at: string;
  capture_channel: string | null;
  email_verified_at: string | null;
  captured_under_config_version: number | string | null;
  payload: unknown;
  contact: unknown;
  fields: unknown;
};

const RECORD = `
  select distinct on (l.attendee_ref)
    ${utcIso("l.occurred_at")}                                    as registered_at,
    l.capture_channel::text                                       as capture_channel,
    ${utcIso("(l.payload ->> 'email_verified_at')::timestamptz")}  as email_verified_at,
    l.captured_under_config_version,
    l.payload,
    attendee_contact(l.activation_id, l.attendee_ref, l.payload -> 'contact') as contact,
    attendee_fields(l.activation_id, l.attendee_ref, l.payload -> 'fields')   as fields
  from spine_live l
  where l.activation_id = $1
    and l.attendee_ref  = $2
    and l.event_type = 'registration_completed'
  order by l.attendee_ref, l.spine_seq desc
`;

/**
 * Every consent act this person performed, in the words they were shown.
 *
 * The join is on `wording_hash` rather than on the consent version, because a
 * consent is the paragraph it was given in: 005 binds the hash to its text with
 * a check constraint, so this is the citation that cannot drift. Changing the
 * wording later publishes a new hash and does not change what this person
 * agreed to, which is the property the whole scheme exists for and the one a
 * screen has to be able to show.
 *
 * A left join, so a consent whose paragraph cannot be produced is still a
 * consent that happened. Inner-joining would delete an act from somebody's
 * history to hide a missing row in a different table.
 */
async function readConsentEvents(
  c: Queryable,
  activationId: string,
  attendeeRef: string,
): Promise<AttendeeConsentEvent[]> {
  const { rows } = await c.query<{
    at: string;
    event: string;
    purpose: string | null;
    consent_version_id: string | null;
    method: string | null;
    capture_channel: string | null;
    wording_hash: string | null;
    locale: string | null;
    body: string | null;
  }>(
    `select
       ${utcIso("l.occurred_at")}               as at,
       l.event_type::text                       as event,
       l.payload ->> 'purpose'                  as purpose,
       l.payload ->> 'consent_version_id'       as consent_version_id,
       l.payload ->> 'method'                   as method,
       l.capture_channel::text                  as capture_channel,
       l.payload ->> 'wording_hash'             as wording_hash,
       w.locale,
       w.body
     from spine_live l
     left join consent_wording w on w.wording_hash = l.payload ->> 'wording_hash'
     where l.activation_id = $1
       and l.attendee_ref  = $2
       and l.event_type in ('consent_captured', 'consent_withdrawn')
     order by l.occurred_at, l.spine_seq`,
    [activationId, attendeeRef],
  );

  return rows.map((row) => ({
    at: row.at,
    event: row.event === "consent_withdrawn" ? "consent_withdrawn" : "consent_captured",
    purpose: row.purpose,
    consent_version_id: row.consent_version_id,
    method: row.method,
    capture_channel: row.capture_channel,
    wording:
      row.wording_hash === null || row.body === null
        ? null
        : { wording_hash: row.wording_hash, locale: row.locale ?? "", body: row.body },
  }));
}

/**
 * The events reduced to the latest word per purpose.
 *
 * Ordered by when the person acted, which the query above already did, so the
 * last event for a purpose is the one that stands. Written as a reduction over
 * the history rather than as its own `distinct on` query for the reason the
 * caller's comment gives: two statements answering one question is two answers
 * to argue about.
 */
function latestPerPurpose(events: readonly AttendeeConsentEvent[]): Record<string, string> {
  const latest: Record<string, string> = {};
  for (const event of events) {
    if (event.purpose === null) continue;
    latest[event.purpose] = event.event;
  }
  return latest;
}

/**
 * The passcode rows for this person's mailbox at this activation, where there is
 * any way to find them.
 *
 * TIMESTAMPS AND NOTHING ELSE. `code_hash`, `link_hash` and `ticket_hash` are
 * not selected, so no future mistake in this file can put them on the wire, and
 * the passcode itself was never stored and is not reconstructible from any of
 * them. `requested_ip` is not selected either: it is the attendee's own address,
 * it answers nothing the design asked for, and 114 keeps it for security audit
 * and rate limiting rather than for a screen.
 *
 * THE JOIN KEY IS A DIGEST, AND WHAT THAT COSTS IS STATED RATHER THAN HIDDEN.
 * 114 gives the verification table no attendee reference on purpose and holds an
 * HMAC of the address rather than the address, so the only way from a person to
 * their passcodes is to recompute the digest under the pepper. This does not
 * weaken that position: the table is unreadable as a list either way, and what
 * is being done here is confirming a single address this reader is already
 * looking at. What it does mean is that an instance with no pepper cannot
 * answer, and saying so is the whole reason this returns a union.
 *
 * AND AN EMPTY RESULT IS NOT NOTHING HAPPENING. Rows are swept a day past
 * expiry, and a registration taken on a stand tablet never had a passcode at
 * all, so an empty list has three quite different causes and the sentence names
 * them.
 */
async function readPasscodes(
  c: Queryable,
  activationId: string,
  email: string | null,
  digestEmail: AttendeeEmailDigest | null,
): Promise<AttendeePasscodeAccount> {
  if (digestEmail === null) {
    return {
      kind: "not_looked_up",
      why: "no_digest",
      says:
        "This instance holds no attendee passcode secret, so it cannot compute the digest that is the only " +
        "way from a person to their passcode rows. Nothing was looked up. This is not a statement that no " +
        "passcode was ever sent to this person.",
    };
  }

  if (email === null) {
    return {
      kind: "not_looked_up",
      why: "no_address",
      says:
        "This registration carries no contact address, so there is nothing to compute a digest from and " +
        "nothing to look up. Passcode rows are found by mailbox, because the verification table holds no " +
        "attendee reference.",
    };
  }

  const { rows } = await c.query<AttendeePasscode>(
    `select
       v.verification_id,
       ${utcIso("v.issued_at")}       as issued_at,
       ${utcIso("v.expires_at")}      as expires_at,
       ${utcIso("v.consumed_at")}     as consumed_at,
       ${utcIso("v.superseded_at")}   as superseded_at,
       ${utcIso("v.ticket_spent_at")} as ticket_spent_at
     from attendee_email_verification v
     where v.activation_id = $1
       and v.email_hash    = $2
     order by v.issued_at, v.verification_id`,
    [activationId, digestEmail(email)],
  );

  return {
    kind: "read",
    passcodes: rows,
    says:
      rows.length === 0
        ? "No passcode row for this mailbox at this activation. Three things look like this and they are " +
          "different: a registration taken on a stand tablet never involved a passcode, a passcode older " +
          "than about a day has been swept, and a mailbox that was never sent one has no row either. " +
          "identity_assurance on the registration is what tells the first case from the other two."
        : "Every passcode row this service still holds for this mailbox at this activation. The passcode " +
          "itself is not stored and cannot be produced. Rows are removed about a day after they expire, so " +
          "this is the surviving history rather than the whole of it. An expiry is not listed as something " +
          "that happened, because nothing writes a row when one expires: expires_at is when it was due to " +
          "stop working.",
  };
}

// ---------------------------------------------------------------- small things

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** A string, or null. Never an empty string, which a spreadsheet and a screen read as a value. */
function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function number(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
}
